/**
 * The journal's side of the cross-tab contract (src/lib/inbox/crossTab.ts): the chat
 * asks to open an appointment or to book a client, this tab acts and acknowledges.
 * Requests left in the mailbox (the chat had to open this tab) are handled on boot.
 */
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CalendarPlus, X } from "lucide-react";
import { useAppointmentModal } from "@/contexts/AppointmentModalContext";
import { apiClient } from "@/lib/apiClient";
import { createCrossTab, serve, takePending, type RequestHandlers } from "@/lib/inbox/crossTab";
import { JOURNAL_WINDOW } from "@/lib/inbox/links";
import { setPendingBooking, usePendingBooking } from "@/lib/inbox/pendingBooking";

export function InboxBridge() {
  const { openModal } = useAppointmentModal();

  useEffect(() => {
    // lets the chat's "open the journal" re-use this tab instead of a new one
    window.name = JOURNAL_WINDOW;
    const handlers: RequestHandlers = {
      "open-appointment": (msg) => {
        openModal(msg.recordId);
        return true;
      },
      "new-booking": (msg) => {
        setPendingBooking(msg.client);
        return true;
      },
    };
    const transport = createCrossTab();
    const stop = serve(transport, "journal", handlers);
    const waiting = takePending();
    if (waiting?.type === "open-appointment") handlers["open-appointment"]?.(waiting);
    else if (waiting?.type === "new-booking") handlers["new-booking"]?.(waiting);
    return () => {
      stop();
      transport.close();
    };
  }, [openModal]);

  // "Open in the journal" from the dashboard's Appointments (boona appointments D7): /?open=<id>
  // opens that appointment's window, as the chat's "open appointment" does
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const open = params.get("open");
    if (!open || !/^[0-9]+$/.test(open)) return;
    params.delete("open");
    const rest = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash);
    openModal(Number(open));
  }, [openModal]);

  // "Book" on a dashboard client card (boona clients CL3b): /?book=<id> - the client waits for
  // a free-time click like a booking handed over from the chat
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const book = params.get("book");
    if (!book || !/^[0-9]+$/.test(book)) return;
    params.delete("book");
    const rest = params.toString();
    window.history.replaceState(null, "", window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash);
    let live = true;
    apiClient
      .get("/api/v1/company/clients/user-base", { params: { client_id: Number(book), per_page: 1 } })
      .then(({ data }) => {
        const row = data?.data?.[0];
        if (!live || !row) return;
        const name = [row.name, row.surname].filter(Boolean).join(" ") || "—";
        setPendingBooking({ id: Number(row.client_id), name, phone: row.phone ?? null });
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  return <PendingBookingBanner />;
}

/** "Pick a time for <client>": the next free-slot click books them (AppointmentCreateModalProvider). */
function PendingBookingBanner() {
  const { t } = useTranslation();
  const client = usePendingBooking();
  if (!client) return null;
  return (
    <div className="fixed bottom-4 left-1/2 z-50 flex max-w-[92vw] -translate-x-1/2 items-center gap-3 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm text-white shadow-lg">
      <CalendarPlus className="h-4 w-4 shrink-0" />
      <span>
        {t("inbox.entry.pending_booking", "Booking for {{name}}: click a free time in the calendar", { name: client.name })}
      </span>
      <button
        type="button"
        aria-label={t("inbox.entry.pending_cancel", "Cancel")}
        onClick={() => setPendingBooking(null)}
        className="rounded p-0.5 hover:bg-white/20"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
